// Design Ref: §4.3 DonutChart — SVG stroke-dasharray 기반 도넛
// Plan SC: FR-03 섹터/시장 분포
import React from 'react';

/**
 * Props:
 *  - data: [{ label, value, color }]
 *  - title?
 *  - size? (default 180)
 *  - formatValue?: (n) => string
 */
export default function DonutChart({
  data = [],
  title,
  size = 180,
  formatValue = (n) => n.toLocaleString(),
}) {
  if (!data.length) {
    return <EmptyBlock title={title} size={size} msg="보유 종목 없음" />;
  }
  const total = data.reduce((a, d) => a + (Number(d.value) || 0), 0);
  if (total === 0) return <EmptyBlock title={title} size={size} msg="값 0" />;

  const R = 60;
  const C = 2 * Math.PI * R;
  let acc = 0;
  const segments = data.map(d => {
    const pct = (Number(d.value) || 0) / total;
    const len = pct * C;
    const offset = C - acc;
    acc += len;
    return { ...d, len, offset, pct };
  });

  return (
    <div style={{ background: 'var(--bg-table)', border: '1px solid var(--border-light)', borderRadius: 12, padding: '14px 16px' }}>
      {title && <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{title}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 12, alignItems: 'center' }}>
        <svg viewBox="0 0 160 160" style={{ width: size, height: size, maxWidth: '100%' }}>
          <circle cx="80" cy="80" r={R} fill="none" stroke="var(--border-light)" strokeWidth="16" />
          {segments.map((s, i) => (
            <circle
              key={s.label + i}
              cx="80" cy="80" r={R}
              fill="none"
              stroke={s.color}
              strokeWidth="16"
              strokeDasharray={`${s.len} ${C}`}
              strokeDashoffset={s.offset}
              transform="rotate(-90 80 80)"
            >
              <title>{`${s.label}: ${formatValue(s.value)} (${(s.pct * 100).toFixed(1)}%)`}</title>
            </circle>
          ))}
          <text x="80" y="80" textAnchor="middle" dominantBaseline="central" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fill: 'var(--text-sub)' }}>
            총 {segments.length}
          </text>
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          {segments.map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, minWidth: 0 }}>
              <div style={{ width: 10, height: 10, background: s.color, borderRadius: 2, flexShrink: 0 }} />
              <div style={{ flex: 1, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</div>
              <div style={{ fontFamily: "'DM Mono', monospace", color: 'var(--text-sub)', fontSize: 10 }}>
                {(s.pct * 100).toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyBlock({ title, size, msg }) {
  return (
    <div style={{ background: 'var(--bg-table)', border: '1px solid var(--border-light)', borderRadius: 12, padding: '14px 16px' }}>
      {title && <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{title}</div>}
      <div style={{ height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>{msg}</div>
    </div>
  );
}
